import { FC, useContext, useEffect, useMemo, useState } from "react";

import { AppContext } from "App";
import { useUser } from "hooks/common/useUser";
import { useAppSelector } from "redux/hooks";
import { useGetTaxCalculationsMutation } from "services/client";
import { IClientDocument } from "types/client";
import { logError } from "utils/sentry";

import { PaymentFailureModal } from "./PaymentFailureModal";
import { PaymentSuccessModal } from "./PaymentSuccessModal";
import { PriceBreakDown } from "./PriceBreakDown";
import { Refund } from "./Refund";
import { ReturnToQuestionnaire } from "./ReturnToQuestionnaire";
import RevolutPaymentComponent from "./RevolutPaymentComponent";

interface PaywallProps {
    showReturnToQuestionnaire: boolean
    currentReturn?: IClientDocument | null
    handleContinue?: () => void
    handlePrev: () => void
}

export const Paywall: FC<PaywallProps> = ({ showReturnToQuestionnaire, currentReturn, handleContinue, handlePrev }) => {
    const { isMobile } = useContext(AppContext);
    const { user } = useUser();
    const { currentDocument } = useAppSelector(state => state.document);

    const [getGetTaxCalculations] = useGetTaxCalculationsMutation();

    const [amount, setAmount] = useState<number>(0);
    const [isLoadingOrderToken, setIsLoadingOrderToken] = useState(true);
    const [showPaymentSuccessModal, setShowPaymentSuccessModal] = useState(false);
    const [genericPaymentFailure, setGenericPaymentFailure] = useState(false);
    const [insufficientFunds, setInsufficientFunds] = useState(false);
    const [taxData, setTaxData] = useState<any | null>(null);
    const [accountantReviewRequested, setAccountantReviewRequested] = useState(false);
    const [pricingCategory, setPricingCategory] = useState<string>("");

    const refundAmount = useMemo(() => {
        return Math.abs(taxData?.totalTaxLiability?.value);
    }, [taxData]);

    const showRefund = useMemo(() => {
        const totalLiability = Number(taxData?.totalTaxLiability?.value);
        return totalLiability < 0 && Math.abs(totalLiability) > amount;
    }, [taxData, amount]);

    useEffect(() => {
        const fetchTaxCalculations = async () => {
            if (!user?.id || !currentDocument.id) {
                logError("Error: User ID or current document is missing. This might indicate an issue with user authentication or document loading.");
                return;
            }
            try {
                const response = await getGetTaxCalculations({
                    clientId: user.id,
                    documentId: currentDocument.id,
                }).unwrap();
                const breakdown = response.breakdown;
                setTaxData(breakdown);
            } catch (error) {
                const errorContext = {
                    clientId: user?.id,
                    message: "Failed to fetch tax calculations",
                    function: "fetchTaxCalculations",
                };
                logError(error, errorContext);
            }
        };

        fetchTaxCalculations();
    }, [getGetTaxCalculations, user, currentDocument]);
    return (
        <div className="p20">
            {showPaymentSuccessModal && (
                <PaymentSuccessModal
                    showPaymentSuccessModal={showPaymentSuccessModal}
                    onSuccess={() => {
                        setShowPaymentSuccessModal(false);
                        handleContinue?.();
                    }}
                />
            )}
            {genericPaymentFailure && (
                <PaymentFailureModal
                    paymentFailed={genericPaymentFailure}
                    setPaymentFailed={setGenericPaymentFailure}
                    message="There was an issue processing your payment. Please check your internet connection and try again."
                    title="Payment Error"
                />
            )}
            {insufficientFunds && (
                <PaymentFailureModal
                    paymentFailed={insufficientFunds}
                    setPaymentFailed={setInsufficientFunds}
                    message="Please check your card details and try again. If this issue continues, please check with your bank."
                    title="Payment Declined"
                />
            )}
            {showRefund && <Refund refundAmount={refundAmount}></Refund>}
            {showReturnToQuestionnaire && <ReturnToQuestionnaire currentReturn={currentReturn}/>}
            <div className={`paywall__price-breakdown${isMobile ? "--mobile" : "--desktop"}`}>
                <PriceBreakDown amount={amount} isLoadingOrderToken={isLoadingOrderToken} setAmount={setAmount}
                    documentId={currentDocument.id}
                    setAccountantReviewRequested={setAccountantReviewRequested}
                    pricingCategory={pricingCategory}
                />
            </div>
            <RevolutPaymentComponent
                key={amount}
                setGenericPaymentFailure={setGenericPaymentFailure}
                setInsufficientFundsPaymentFailure={setInsufficientFunds}
                setShowPaymentSuccessModal={setShowPaymentSuccessModal}
                setIsLoadingOrderToken={setIsLoadingOrderToken}
                setAmount={setAmount}
                amount={amount}
                documentId={currentDocument.id}
                handlePrev={handlePrev}
                handleContinue={handleContinue!}
                accountantReviewRequested={accountantReviewRequested}
                setPricingCategory={setPricingCategory}
            />
        </div>
    );
};
